const PLAN_FILTER_STORAGE_KEY = 'global-modal-filters';

export interface SavedPlanBlockFilters {
  selectedBlockDimension?: Record<string, string[]>[];
}

export interface BlockDimensionMeta {
  name: string;
  items: string[];
}

/**
 * Reads persisted plan-page dimension filter selections for a model/block pair.
 */
export const readSavedPlanBlockFilters = (
  modelId: string | number | undefined,
  blockId: string | number | undefined,
): SavedPlanBlockFilters | null => {
  if (modelId == null || blockId == null) return null;

  try {
    const raw = localStorage.getItem(PLAN_FILTER_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as Record<string, Record<string, SavedPlanBlockFilters>>;
    return data[String(modelId)]?.[String(blockId)] ?? null;
  } catch {
    return null;
  }
};

/**
 * Merges saved subset selections with the current block dimension metadata so Redux
 * and the filter modal always use the live dimension names/item lists.
 */
export const mergeSavedFiltersWithBlockDimensions = (
  savedSelections: Record<string, string[]>[] | undefined,
  dimensions: BlockDimensionMeta[],
): Record<string, string[]>[] => {
  if (dimensions.length === 0) return savedSelections ?? [];

  const savedDraft: Record<string, string[]> = {};
  (savedSelections ?? []).forEach((entry) => {
    const name = Object.keys(entry ?? {})[0];
    if (name && Array.isArray(entry[name])) {
      savedDraft[name] = [...entry[name]];
    }
  });

  return dimensions
    .filter((dimension) => (dimension.items?.length ?? 0) > 0)
    .map((dimension) => {
      const savedItems = savedDraft[dimension.name];
      const validSavedItems = savedItems?.filter((item) => dimension.items.includes(item)) ?? [];

      if (validSavedItems.length > 0 && validSavedItems.length < dimension.items.length) {
        return { [dimension.name]: validSavedItems };
      }

      return { [dimension.name]: [...dimension.items] };
    });
};

/**
 * Returns saved dimension entries with non-empty item lists for dim_filter API params.
 * Used before FetchBlockItemDimensions completes; backend validates item names.
 */
export const readSavedDimFilterForApi = (
  modelId: string | number | undefined,
  blockId: string | number | undefined,
): Record<string, string[]>[] => {
  const saved = readSavedPlanBlockFilters(modelId, blockId);
  if (!saved?.selectedBlockDimension?.length) return [];

  return saved.selectedBlockDimension.filter((entry) => {
    const [, items] = Object.entries(entry ?? {})[0] ?? [];
    return Array.isArray(items) && items.length > 0;
  });
};

/**
 * Keeps only strict-subset dimension filters for localStorage (omits "select all").
 */
export const persistActiveDimFilterSubsets = (
  selections: Record<string, string[]>[],
  dimensions: BlockDimensionMeta[] | undefined,
): Record<string, string[]>[] => {
  if (!dimensions?.length) {
    return selections.filter((entry) => {
      const [, items] = Object.entries(entry ?? {})[0] ?? [];
      return Array.isArray(items) && items.length > 0;
    });
  }

  return selections.filter((entry) => {
    const [name, items] = Object.entries(entry ?? {})[0] ?? [];
    if (!name || !Array.isArray(items) || items.length === 0) return false;

    const meta = dimensions.find((dimension) => dimension.name === name);
    const totalItems = meta?.items?.length ?? 0;
    return totalItems > 0 && items.length < totalItems;
  });
};
